import type { OcSelfserviceSettings } from "@oc/types";

const OC_API_BASE = import.meta.env.VITE_OC_API_BASE ?? "/api";

export const OC_SESSION_KEY = "oc_session_token";
const OC_SESSION_CREATED_KEY = "oc_session_created";
const OC_LAST_ACTIVITY_KEY = "oc_last_activity";
const OC_SESSION_EXPIRES_KEY = "oc_session_expires";

export const OC_SESSION_MAX_MS = 24 * 60 * 60 * 1000;   // 24 hours
export const OC_INACTIVITY_MAX_MS = 8 * 60 * 60 * 1000; // 8 hours

/** How long before the deadline the "session ending" warning appears. */
export const OC_SESSION_WARNING_MS = 5 * 60 * 1000; // 5 minutes

export function ocGetSessionToken(): string | null {
  return localStorage.getItem(OC_SESSION_KEY);
}

export function ocSetSessionToken(token: string, expiresAt?: number): void {
  const now = String(Date.now());
  localStorage.setItem(OC_SESSION_KEY, token);
  localStorage.setItem(OC_SESSION_CREATED_KEY, now);
  localStorage.setItem(OC_LAST_ACTIVITY_KEY, now);
  ocSetServerSessionExpiry(expiresAt ?? null);
  // A new session gets a fresh chance to report its own loss.
  ocResetSessionLost();
}

/**
 * Record the server's own expiry for this session (epoch ms), as reported by
 * login or `/tenant/auth/me`. Preferred over the local sign-in clock: it
 * survives a clock skew and stays right if the server TTL ever changes.
 */
export function ocSetServerSessionExpiry(expiresAt: number | null): void {
  if (expiresAt && Number.isFinite(expiresAt)) {
    localStorage.setItem(OC_SESSION_EXPIRES_KEY, String(expiresAt));
  } else {
    localStorage.removeItem(OC_SESSION_EXPIRES_KEY);
  }
}

export function ocClearSessionToken(): void {
  localStorage.removeItem(OC_SESSION_KEY);
  localStorage.removeItem(OC_SESSION_CREATED_KEY);
  localStorage.removeItem(OC_LAST_ACTIVITY_KEY);
  localStorage.removeItem(OC_SESSION_EXPIRES_KEY);
}

export function ocRecordActivity(): void {
  localStorage.setItem(OC_LAST_ACTIVITY_KEY, String(Date.now()));
}

/** Why the session is going to end, and when (epoch ms). */
export type OcSessionEndReason = "inactivity" | "absolute";

export interface OcSessionDeadline {
  expiresAt: number;
  reason: OcSessionEndReason;
}

/**
 * The earlier of the two clocks that end a session: the idle timer (renewable
 * — any activity pushes it out) and the absolute session lifetime (a hard cap
 * that nothing extends). Returns null when there is no session at all.
 */
export function ocGetSessionDeadline(): OcSessionDeadline | null {
  if (!ocGetSessionToken()) return null;

  const created = Number(localStorage.getItem(OC_SESSION_CREATED_KEY) ?? "0");
  const lastActivity = Number(localStorage.getItem(OC_LAST_ACTIVITY_KEY) ?? created);
  const serverExpires = Number(localStorage.getItem(OC_SESSION_EXPIRES_KEY) ?? "0");

  const absoluteAt =
    serverExpires > 0 ? serverExpires : created > 0 ? created + OC_SESSION_MAX_MS : Infinity;
  const inactivityAt = lastActivity > 0 ? lastActivity + OC_INACTIVITY_MAX_MS : Infinity;

  if (!Number.isFinite(absoluteAt) && !Number.isFinite(inactivityAt)) return null;

  return inactivityAt <= absoluteAt
    ? { expiresAt: inactivityAt, reason: "inactivity" }
    : { expiresAt: absoluteAt, reason: "absolute" };
}

export interface OcRenewResponse {
  renewed: boolean;
  expiresAt: number | null;
  renewableUntil: number;
}

/**
 * Ask the server to slide this session's expiry forward. Returns renewed:false
 * once the session has reached its renewal cap — there is nothing left to grant
 * and the caller should end the session rather than retry.
 */
export async function ocRenewSession(): Promise<OcRenewResponse> {
  const res = await ocFetch<OcRenewResponse>("/tenant/auth/renew", { method: "POST" });
  if (res.renewed && res.expiresAt) ocSetServerSessionExpiry(res.expiresAt);
  return res;
}

/** Milliseconds until the session ends; 0 once it has. */
export function ocGetSessionRemainingMs(): number {
  const deadline = ocGetSessionDeadline();
  if (!deadline) return 0;
  return Math.max(0, deadline.expiresAt - Date.now());
}

export function ocIsSessionExpired(): boolean {
  const deadline = ocGetSessionDeadline();
  if (!deadline) return false;
  return Date.now() >= deadline.expiresAt;
}

// ─── Session-lost notification ────────────────────────────────────────────────

/**
 * The client clock only predicts when a session ends; the server decides. A
 * session can die early — revoked, an impersonation window closed, the KV entry
 * evicted — and the first sign of it is a 401 on whatever the page asked for
 * next. Without this, that 401 renders as "Failed to load…" inside a card while
 * the user sits in a portal that can no longer do anything.
 */
type OcSessionLostListener = () => void;

const ocSessionLostListeners = new Set<OcSessionLostListener>();
let ocSessionLostFired = false;

export function ocOnSessionLost(listener: OcSessionLostListener): () => void {
  ocSessionLostListeners.add(listener);
  return () => ocSessionLostListeners.delete(listener);
}

/** Re-arm after a fresh sign-in, so the next session can report its own loss. */
export function ocResetSessionLost(): void {
  ocSessionLostFired = false;
}

function ocNotifySessionLost(): void {
  // A page in flight usually has several requests going; they all 401 together.
  // Only the first one ends the session.
  if (ocSessionLostFired) return;
  ocSessionLostFired = true;
  ocSessionLostListeners.forEach((listener) => listener());
}

/**
 * Endpoints where a 401 means "those credentials are wrong", not "your session
 * ended" — signing the user out there would wipe the error they need to read.
 */
const OC_AUTH_PUBLIC_PATHS = [
  "/tenant/auth/login",
  "/tenant/auth/sso/exchange",
  "/tenant/auth/forgot-password",
  "/tenant/auth/reset-password",
  "/tenant/auth/accept-invite",
];

function ocIsSessionLoss(path: string, status: number, sentToken: boolean): boolean {
  if (status !== 401 || !sentToken) return false;
  return !OC_AUTH_PUBLIC_PATHS.some((p) => path.startsWith(p));
}

export class OcApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    // Parsed JSON body of the error response, if any. Callers can read
    // structured fields like `error` and `missing` for preconditions failures.
    public readonly body?: unknown
  ) {
    super(message);
    this.name = "OcApiError";
  }
}

export async function ocFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const extraHeaders: Record<string, string> = {};
  const token = ocGetSessionToken();
  if (token) extraHeaders["X-OC-Session-Token"] = token;

  const response = await fetch(`${OC_API_BASE}${path}`, {
    headers: { "Content-Type": "application/json", ...extraHeaders, ...options?.headers },
    ...options,
  });
  if (!response.ok) {
    const text = await response.text().catch(() => "Unknown error");
    let parsed: unknown;
    try { parsed = JSON.parse(text); } catch { /* not JSON */ }
    if (ocIsSessionLoss(path, response.status, Boolean(token))) ocNotifySessionLost();
    throw new OcApiError(response.status, `${response.status}: ${text}`, parsed);
  }
  return response.json() as Promise<T>;
}

export function ocApiKey(path: string): readonly ["oc-api", string] {
  return ["oc-api", path] as const;
}

/** Convenience wrapper for POST/PUT/PATCH with JSON body. */
export async function ocMutate<TBody, TResponse>(
  path: string,
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  body?: TBody
): Promise<TResponse> {
  return ocFetch<TResponse>(path, {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
}

// ─── Namespaced API surface ───────────────────────────────────────────────────

export interface OcSelfserviceEligibleConnection {
  id: string;
  name: string;
  type: string;
  hasPasswordReset: boolean;
}

export interface OcSelfserviceTwilioConnectionRef {
  id: string;
  name: string;
}

export interface OcSelfserviceSmtpConnectionRef {
  id: string;
  name: string;
  provider: string;
}

export interface OcSelfserviceSettingsResponse {
  settings: OcSelfserviceSettings;
  eligibleConnections: OcSelfserviceEligibleConnection[];
  twilioConnections: OcSelfserviceTwilioConnectionRef[];
  smtpConnections: OcSelfserviceSmtpConnectionRef[];
}

// ─── Reset Log types ──────────────────────────────────────────────────────────

export type OcSelfserviceResetLogStatus =
  | "pending"
  | "verified"
  | "success"
  | "failed"
  | "expired"
  | "blocked";

export interface OcSelfserviceResetLogEntry {
  id: string;
  tenantId: string;
  createdAt: string;
  completedAt: string | null;
  identifierSubmitted: string | null;
  resolvedUserDn: string | null;
  connectionId: string | null;
  proofingMethod: string | null;
  proofingTargetMasked: string | null;
  verifyAttempts: number | null;
  ip: string | null;
  userAgent: string | null;
  country: string | null;
  status: OcSelfserviceResetLogStatus;
  failureReason: string | null;
  dispatchedCommandId: string | null;
  durationMs: number | null;
}

export interface OcSelfserviceResetLogSparklinePoint {
  date: string; // YYYY-MM-DD
  success: number;
  failed: number;
  expired: number;
  blocked: number;
}

export interface OcSelfserviceResetLogPatterns {
  repeatResets: {
    count: number;
    users: Array<{ resolvedUserDn: string; resetCount: number }>;
  };
  failedVerifySpike: {
    count: number;
    ips: Array<{ ip: string; attemptCount: number; country: string | null }>;
  };
  resetThenPrivChange: {
    count: number;
    pairs: Array<{ resolvedUserDn: string; resetAt: string; privChangeAt: string }>;
    note?: string;
  };
}

export interface OcSelfserviceResetLogResponse {
  rows: OcSelfserviceResetLogEntry[];
  total: number;
  page: number;
  pageSize: number;
  sparkline: OcSelfserviceResetLogSparklinePoint[];
  patterns: OcSelfserviceResetLogPatterns;
}

export interface OcSelfserviceResetLogQueryParams {
  from?: string;
  to?: string;
  status?: string;
  q?: string;
  page?: number;
  pageSize?: number;
}

function ocBuildResetLogQuery(
  params: OcSelfserviceResetLogQueryParams,
  extra?: Record<string, string>
): string {
  const search = new URLSearchParams();
  if (params.from) search.set("from", params.from);
  if (params.to) search.set("to", params.to);
  if (params.status) search.set("status", params.status);
  if (params.q) search.set("q", params.q);
  if (params.page !== undefined) search.set("page", String(params.page));
  if (params.pageSize !== undefined) search.set("pageSize", String(params.pageSize));
  if (extra) {
    for (const [k, v] of Object.entries(extra)) search.set(k, v);
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

export const ocApi = {
  selfservice: {
    getSettings: (): Promise<OcSelfserviceSettingsResponse> =>
      ocFetch<OcSelfserviceSettingsResponse>("/tenant/selfservice/settings"),
    updateSettings: (
      patch: Partial<OcSelfserviceSettings>
    ): Promise<{ settings: OcSelfserviceSettings }> =>
      ocMutate<Partial<OcSelfserviceSettings>, { settings: OcSelfserviceSettings }>(
        "/tenant/selfservice/settings",
        "PUT",
        patch
      ),
    getResetLog: (
      params: OcSelfserviceResetLogQueryParams = {}
    ): Promise<OcSelfserviceResetLogResponse> =>
      ocFetch<OcSelfserviceResetLogResponse>(
        `/tenant/selfservice/reset-log${ocBuildResetLogQuery(params)}`
      ),
    downloadResetLogCsv: async (
      params: Omit<OcSelfserviceResetLogQueryParams, "page" | "pageSize"> = {}
    ): Promise<Blob> => {
      const token = ocGetSessionToken();
      const headers: Record<string, string> = {};
      if (token) headers["X-OC-Session-Token"] = token;
      const qs = ocBuildResetLogQuery(params, { format: "csv" });
      const response = await fetch(
        `${OC_API_BASE}/tenant/selfservice/reset-log${qs}`,
        { headers }
      );
      if (!response.ok) {
        const text = await response.text().catch(() => "Unknown error");
        if (response.status === 401 && token) ocNotifySessionLost();
        throw new OcApiError(response.status, `${response.status}: ${text}`);
      }
      return response.blob();
    },
  },
};
